import { LIMITS } from '../../core/data/limits';
import type { KeywordAnalysis } from '../../core/types';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { cn } from '../../lib/cn';

/**
 * Target-role keyword coverage.
 *
 * Recruiters search LinkedIn by keyword, so a profile that never uses the words
 * for its own field is invisible regardless of how well written it is. This panel
 * makes the gap concrete: here are the terms for the role you picked, here are
 * the ones you already use, here are the ones you don't.
 *
 * It renders nothing without a target role. An empty keyword panel would imply
 * the user is missing everything, when in fact they were never asked.
 */
export function KeywordPanel({ keywords }: { keywords: KeywordAnalysis }) {
  if (!keywords.roleId || !keywords.roleLabel) return null;

  const percent = Math.round(keywords.coverage * 100);
  const target = Math.round(LIMITS.keywords.coverageTarget * 100);
  const meetsTarget = keywords.coverage >= LIMITS.keywords.coverageTarget;

  return (
    <Card>
      <CardBody className="space-y-4">
        <CardHeader
          as="h3"
          title="Keyword coverage"
          description={`Against the terms recruiters search for ${keywords.roleLabel} roles.`}
          aside={
            <Badge tone={meetsTarget ? 'success' : 'important'}>
              {percent}% of {keywords.present.length + keywords.missing.length}
            </Badge>
          }
        />

        <div>
          <div className="h-2 overflow-hidden rounded-pill bg-sunken">
            <div
              className={cn(
                'h-full rounded-pill transition-[width] duration-700',
                meetsTarget ? 'bg-success-500' : 'bg-important-500'
              )}
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-subtle">
            {meetsTarget
              ? `Above the ${target}% we look for. Adding more of the terms below still helps.`
              : `Aim for at least ${target}%. Work the missing terms into your About and Experience — naturally, in a real sentence.`}
          </p>
        </div>

        {keywords.missing.length > 0 ? (
          <div>
            <h4 className="text-xs font-semibold tracking-wide text-subtle uppercase">
              Missing ({keywords.missing.length})
            </h4>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {keywords.missing.map((keyword) => (
                <li key={keyword}>
                  <Badge tone="important">{keyword}</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {keywords.present.length > 0 ? (
          <div>
            <h4 className="text-xs font-semibold tracking-wide text-subtle uppercase">
              Already there ({keywords.present.length})
            </h4>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {keywords.present.map((keyword) => (
                <li key={keyword}>
                  <Badge tone="success">{keyword}</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="text-xs text-subtle">
          Keyword stuffing does not work — LinkedIn ranks on relevance, and a human reads the
          result. Use a term only where it is true.
        </p>
      </CardBody>
    </Card>
  );
}
